import {
  DATE_ORDER_LABELS,
  ENUM_LABELS,
  type ImportRunView,
  dateOrderSchema,
  importWriteModeSchema,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Select } from '#client/components/ui/Select';
import type { ImportRunState } from '../useImportRun';

const DATE_ORDER_OPTIONS = dateOrderSchema.options.map((order) => ({
  value: order,
  label: DATE_ORDER_LABELS[order],
}));

const WRITE_MODE_OPTIONS = importWriteModeSchema.options.map((mode) => ({
  value: mode,
  label: ENUM_LABELS.importWriteMode[mode],
}));

/** The options that apply to every row: date order, write mode, what may be created. */
export function MapOptionsCard({
  run,
  isBusy,
  onChange,
}: {
  run: ImportRunView;
  isBusy: boolean;
  onChange: ImportRunState['updateMapping'];
}) {
  return (
    <Card title="How to read it" subtitle="These apply to every row">
      <div className="c-form-grid">
        <Select
          label="Dates are written"
          options={DATE_ORDER_OPTIONS}
          value={run.options.date_order}
          onChange={(choice) => void onChange({ options: { date_order: choice } })}
          disabled={isBusy}
          helpText="03/04/2024 is a different day under each of these, and the file does not say which"
        />

        <Select
          label="Rows that already exist"
          options={WRITE_MODE_OPTIONS}
          value={run.options.write_mode}
          onChange={(choice) => void onChange({ options: { write_mode: choice } })}
          disabled={isBusy}
        />
      </div>

      <Checkbox
        label="Leave existing values alone where the sheet is blank"
        checked={run.options.preserve_existing}
        onChange={(event) =>
          void onChange({ options: { preserve_existing: event.target.checked } })
        }
        disabled={isBusy}
      />
      <p className="c-field__help">
        On. Off means an empty cell erases whatever is stored — which is how a nine-row price
        correction wipes a column across the whole catalogue.
      </p>

      <Checkbox
        label="Create departments named in heading rows"
        checked={run.options.create_departments}
        onChange={(event) =>
          void onChange({ options: { create_departments: event.target.checked } })
        }
        disabled={isBusy}
      />

      {run.entity === 'patients' && (
        <Checkbox
          label="Create tests that are not in the catalogue"
          checked={run.options.create_missing_tests}
          onChange={(event) =>
            void onChange({ options: { create_missing_tests: event.target.checked } })
          }
          disabled={isBusy}
        />
      )}
      {run.entity === 'patients' && (
        <p className="c-field__help">
          Off. A typo in a history sheet would otherwise add a priceless test that the front desk
          can immediately book.
        </p>
      )}
    </Card>
  );
}
